import { useQuery } from '@apollo/client';
import cn from 'classnames';
import Link from 'next/link';
// React
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
// Types
import {
  Gender,
  MemorabiliaFilterInput,
  MemorabiliaOrderBy,
  MemorabiliaStatus,
  MerchProductOrderBy,
  SortDirection,
  StoreStatus,
} from 'api/graphql-global-types';
import {
  GetMerchMinimalInfo,
  GetMerchMinimalInfoVariables,
  GetMerchMinimalInfo_getMerchProducts_entities,
} from 'api/merch/types/GetMerchMinimalInfo';
import { GetMemorabiliaMinimalInfo_getMemorabilia_entities_images } from 'api/memorabilia/types/GetMemorabiliaMinimalInfo';
import { GetExperiencesMinimalInfo_getExperiences_entities_images } from 'api/experiences/types/GetExperiencesMinimalInfo';
// Api
import { GET_MERCH_MINIMAL_INFO } from 'api/merch/queries';
// Hooks
import {
  useGetAthleteMemorabilia,
  useBreakpoints,
  useCarouselArrows,
} from 'hooks';
import { useGetExperience } from 'hooks/useGetExperiences';
// Ui
import Slider from 'react-slick';
import Loader from 'ui3/Loader/Loader';
import Text from 'ui3/Text/Text';
import LinkButton from 'ui3/LinkButton/LinkButton';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import MerchListItem from 'components/common3/MerchList/components/MerchListItem/MerchListItem';
import MemorabiliaListItem from 'components/common3/MemorabiliaList/components/MemorabiliaListItem';
import ExperienceListItem from 'components/common3/ExperienceList/components/ExperienceListItem/ExperienceListItem';
import CarouselSlider from 'components/common3/CarouselSlider/CarouselSlider';
// Styles
import styles from './ShopNowSection.module.scss';

type ShopNowSectionProps = {
  seeAllLink: string;
  className?: string;
  storeId: string;
};

const ShopNowSection = ({
  seeAllLink,
  className,
  storeId,
}: ShopNowSectionProps) => {
  const { isBreakpointL } = useBreakpoints();
  const sliderRef = useRef<InstanceType<typeof Slider> | null>(null);
  const [arrowControls, setArrowControls] = useState<{
    prev: () => void;
    next: () => void;
  } | null>(null);

  const memorabiliaVariables: MemorabiliaFilterInput = {
    statuses: [MemorabiliaStatus.Active],
    limit: 8,
    isOrphanPage: null,
    orderBy: MemorabiliaOrderBy.createdAt,
    direction: SortDirection.DESC,
  };

  const { data: merchData, loading: merchLoading } = useQuery<
    GetMerchMinimalInfo,
    GetMerchMinimalInfoVariables
  >(GET_MERCH_MINIMAL_INFO, {
    variables: {
      input: {
        orderBy: MerchProductOrderBy.Date,
        storeIds: [storeId],
        gender: [Gender.Male, Gender.Female],
        limit: 8,
      },
    },
  });

  const {
    data: memorabiliaData,
    loading: memorabiliaLoading,
  } = useGetAthleteMemorabilia({
    storeId,
    variables: memorabiliaVariables,
  });

  const { data: experienceData, loading: experienceLoading } = useGetExperience(
    {
      variables: {
        input: {
          storeIds: [storeId],
          storeStatuses: [StoreStatus.Active],
          limit: 8,
        },
      },
    }
  );

  const getMemorabiliaHoverImage = (
    images:
      | GetMemorabiliaMinimalInfo_getMemorabilia_entities_images[]
      | null
      | undefined
  ): string | undefined => {
    if (!images || images.length < 2) return undefined;

    const nonMainImages = images.filter((img) => !img.isMainImage);
    if (nonMainImages.length === 0) return undefined;

    return nonMainImages[0]?.url || undefined;
  };

  const getExperienceHoverImage = (
    images:
      | GetExperiencesMinimalInfo_getExperiences_entities_images[]
      | null
      | undefined
  ): string | undefined => {
    if (!images || images.length < 2) return undefined;

    const nonMainImages = images.filter((img) => !img.isMainImage);
    if (nonMainImages.length === 0) return undefined;

    return nonMainImages[0]?.experienceImageFileKey || undefined;
  };

  const getMerchHoverImage = (
    merchItem: GetMerchMinimalInfo_getMerchProducts_entities | undefined
  ): string | undefined => {
    if (
      !merchItem?.merchProductVariants ||
      merchItem.merchProductVariants.length === 0
    ) {
      return undefined;
    }

    const defaultVariant = merchItem.merchProductVariants[0];
    if (
      !defaultVariant.mockupImages ||
      defaultVariant.mockupImages.length === 0
    ) {
      return undefined;
    }

    // Use product's mainImageUrl as the main image reference
    const mainImageUrl = merchItem.mainImageUrl;

    // Find an image that's different from the main image
    const otherImage = defaultVariant.mockupImages.find(
      (img) => img.mockupImageURL && img.mockupImageURL !== mainImageUrl
    );

    return otherImage?.mockupImageURL || undefined;
  };

  const { memorabilia, merch, experiences, totalItems } = useMemo(() => {
    const memorabilia = memorabiliaData?.getMemorabilia?.entities || [];
    const merch = merchData?.getMerchProducts.entities || [];
    const experiences = experienceData?.getExperiences?.entities || [];

    return {
      memorabilia,
      merch,
      experiences,
      totalItems: memorabilia.length + merch.length + experiences.length,
    };
  }, [memorabiliaData, merchData, experienceData]);

  const isLoading = merchLoading || memorabiliaLoading || experienceLoading;
  const isEmptyList = !isLoading && totalItems === 0;

  const { canScroll, atStart, atEnd, onBeforeChange } = useCarouselArrows(
    sliderRef,
    totalItems
  );

  const handlePrev = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, []);

  const handleNext = useCallback(() => {
    sliderRef.current?.slickNext();
  }, []);

  useEffect(() => {
    if (canScroll) {
      setArrowControls({ prev: handlePrev, next: handleNext });
    } else {
      setArrowControls(null);
    }
  }, [canScroll, handlePrev, handleNext]);

  return (
    <section className={cn(styles.root, className)} key={`shop-now-${storeId}`}>
      <div className={styles.header}>
        <Text as="h2" variant="h5" color="lights-high">
          Shop Now
        </Text>
        <div className={styles.headerRight}>
          {arrowControls && isBreakpointL && (
            <div className={styles.arrowButtons}>
              <button
                type="button"
                onClick={arrowControls.prev}
                disabled={atStart}
                className={styles.arrowButton}
                aria-label="Previous"
              >
                <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
              </button>
              <button
                type="button"
                onClick={arrowControls.next}
                disabled={atEnd}
                className={styles.arrowButton}
                aria-label="Next"
              >
                <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
              </button>
            </div>
          )}
          {isBreakpointL && (
            <LinkButton
              variant="secondary"
              subVariant="outlined"
              link={seeAllLink}
              icon="chevronRight"
              iconPosition="right"
              size="medium"
              iconSize={16}
              className={styles.seeShopButton}
            >
              View Shop
            </LinkButton>
          )}
          {!isBreakpointL && (
            <Link href={seeAllLink} passHref>
              <a className={styles.mobileTextWithArrow}>
                <span className={styles.mobileText}>View Shop</span>
                <MillionsIcon
                  name="arrowRight"
                  className={styles.mobileArrowIcon}
                />
              </a>
            </Link>
          )}
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.productsListContainer}>
          <Loader loading={isLoading} size="large" />
          {!isEmptyList && (
            <div className={styles.rootCarousel}>
              <CarouselSlider
                ref={sliderRef}
                settings={{ beforeChange: onBeforeChange }}
              >
                {memorabilia?.map((item) => {
                  const hoverImage = getMemorabiliaHoverImage(item.images);
                  return (
                    <MemorabiliaListItem
                      key={`memorabilia-${item.id}-${storeId}`}
                      memorabilia={item}
                      itemType="default"
                      openInNewTab
                      imageLazyLoading
                      showBuyNowButton={true}
                      hoverImageUrl={hoverImage}
                    />
                  );
                })}
                {merch.map((merchItem) => {
                  const hoverImage = getMerchHoverImage(merchItem);
                  return (
                    <MerchListItem
                      key={`merch-${merchItem.id}-${storeId}`}
                      merch={merchItem}
                      openInNewTab
                      imageLazyLoading
                      type="default"
                      showBuyNowButton={true}
                      hoverImageUrl={hoverImage}
                    />
                  );
                })}
                {experiences.map((experience) => {
                  const hoverImage = getExperienceHoverImage(experience.images);
                  return (
                    <ExperienceListItem
                      key={`experience-${experience.id}-${storeId}`}
                      experience={experience}
                      type="default"
                      showUser={true}
                      showBuyNowButton={true}
                      hoverImageUrl={hoverImage}
                    />
                  );
                })}
              </CarouselSlider>
            </div>
          )}
          {arrowControls && !isBreakpointL && (
            <div className={styles.mobileArrows}>
              <button
                type="button"
                onClick={arrowControls.prev}
                className={styles.arrowButton}
                aria-label="Previous"
              >
                <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
              </button>
              <button
                type="button"
                onClick={arrowControls.next}
                className={styles.arrowButton}
                aria-label="Next"
              >
                <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ShopNowSection;
